/**
 * Helpers for reading the `calculatedServiceCharge` that the payment data
 * response carries for each payment option, including Mobile Money options that
 * nest their providers per country.
 */

/** Flattens a payment provider's options, unwrapping nested Mobile Money providers. */
const flattenOptions = (paymentProvider: any): any[] => {
  const options: any[] = paymentProvider?.paymentOptions ?? [];
  return options.flatMap((option: any) =>
    Array.isArray(option?.paymentProviders) ? option.paymentProviders : [option],
  );
};

/** Finds a single payment option across all providers by its provider id uuid. */
export const getSelectedPaymentOption = (
  payment: any[],
  uuid?: string,
): any | undefined => {
  if (!uuid || !Array.isArray(payment)) return undefined;
  for (const paymentProvider of payment) {
    const match = flattenOptions(paymentProvider).find(
      (option: any) => option?.paymentProviderInfo?.id?.uuid === uuid,
    );
    if (match) return match;
  }
  return undefined;
};

/** A method whose providers are chosen per country (Mobile Money) has no fixed charge upfront. */
const isSelectionGated = (paymentProvider: any): boolean =>
  (paymentProvider?.paymentOptions ?? []).some((option: any) =>
    Array.isArray(option?.paymentProviders),
  );

/**
 * Service charge to display on a payment method's badge. 
 */
export const getProviderServiceCharge = (
  paymentProvider: any,
  selectedUuid?: string,
): number | null => {
  const options = flattenOptions(paymentProvider);
  const selected = selectedUuid
    ? options.find(
        (option: any) => option?.paymentProviderInfo?.id?.uuid === selectedUuid,
      )
    : undefined;
  if (!selected && isSelectionGated(paymentProvider)) return null;
  return (selected ?? options[0])?.calculatedServiceCharge ?? 0;
};

/**
 * `totalAmount` (order amount + service charge) the selected payment option
 * carries, used as the "total to pay" in the summary.
 */
export const getProviderTotalAmount = (
  paymentProvider: any,
  selectedUuid?: string,
): number | null => {
  const options = flattenOptions(paymentProvider);
  const selected = selectedUuid
    ? options.find(
        (option: any) => option?.paymentProviderInfo?.id?.uuid === selectedUuid,
      )
    : undefined;
  if (!selected && isSelectionGated(paymentProvider)) return null;
  return (selected ?? options[0])?.totalAmount ?? null;
};
